class CustomTabComponent extends HTMLElement {
  /** @type {number} */
  #activeIndex = 0;

  connectedCallback() {
    this.#relocateTriggers();
    this.#bindTabs();
    this.#setActive(0, { instant: true });
    this.#applyMobileLayout();
    window.addEventListener('resize', this.#onResize);
  }

  #relocateTriggers() {
    const header = this.querySelector('.custom-tab__header');
    if (!header) return;

    const wrappers = Array.from(this.querySelectorAll('[data-custom-tab-trigger-wrapper]'));
    wrappers.forEach((wrapper) => {
      const trigger = wrapper.querySelector('[role="tab"]');
      if (trigger) {
        header.appendChild(trigger);
      }
      wrapper.remove();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
  }

  #onResize = () => {
    this.#applyMobileLayout();
  };

  #bindTabs() {
    const tabs = this.#getTabs();
    const panels = this.#getPanels();

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        this.#setActive(index);
      });

      tab.addEventListener('keydown', (event) => {
        if (!(event instanceof KeyboardEvent)) return;
        let next = index;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          next = (index + 1) % tabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          next = (index - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = tabs.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        tabs[next]?.focus();
        this.#setActive(next);
      });
    });

    if (this.dataset.mobileLayout === 'accordion' && window.matchMedia('(max-width: 749px)').matches) {
      panels.forEach((panel, index) => {
        const trigger = panel.querySelector('[data-custom-tab-accordion-trigger]');
        trigger?.addEventListener('click', () => {
          const isOpen = panel.hasAttribute('data-open');
          panels.forEach((p) => p.removeAttribute('data-open'));
          if (!isOpen) {
            panel.setAttribute('data-open', '');
            this.#activeIndex = index;
          }
        });
      });
    }
  }

  #getTabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  #getPanels() {
    return Array.from(this.querySelectorAll('[role="tabpanel"]'));
  }

  /**
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  #setActive(index, options = {}) {
    const tabs = this.#getTabs();
    const panels = this.#getPanels();
    if (!tabs.length || !panels.length) return;

    const safeIndex = Math.max(0, Math.min(index, tabs.length - 1));
    this.#activeIndex = safeIndex;

    const isAccordionMobile =
      this.dataset.mobileLayout === 'accordion' && window.matchMedia('(max-width: 749px)').matches;

    tabs.forEach((tab, i) => {
      const isActive = i === safeIndex;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel, i) => {
      const isActive = i === safeIndex;
      const accordionTrigger = panel.querySelector('[data-custom-tab-accordion-trigger]');
      accordionTrigger?.setAttribute('aria-expanded', String(isActive));

      if (isAccordionMobile) {
        if (isActive) {
          panel.setAttribute('data-open', '');
        } else {
          panel.removeAttribute('data-open');
        }
        panel.removeAttribute('hidden');
        panel.removeAttribute('inert');
      } else {
        panel.toggleAttribute('hidden', !isActive);
        panel.toggleAttribute('inert', !isActive);
      }
    });

    if (!options.instant && !isAccordionMobile) {
      const panel = panels[safeIndex];
      panel?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  #applyMobileLayout() {
    const isAccordionMobile =
      this.dataset.mobileLayout === 'accordion' && window.matchMedia('(max-width: 749px)').matches;
    this.classList.toggle('custom-tab--accordion-mobile', isAccordionMobile);
    if (isAccordionMobile) {
      this.#setActive(this.#activeIndex, { instant: true });
    } else {
      this.#setActive(this.#activeIndex, { instant: true });
    }
  }

  /**
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  select(index, options = {}) {
    this.#setActive(index, options);
  }
}

if (!customElements.get('custom-tab-component')) {
  customElements.define('custom-tab-component', CustomTabComponent);
}
